<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-height="680"
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >

    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="list-tenantLabels-createCategory-activator">
        <slot></slot>
      </div>
    </template>

    <v-form ref="form"
      v-on:submit.prevent="updateCategory"
      autocomplete="off"
      data-cy="dialog-createCategory-form">

      <v-card v-cloak data-cy="dialog-tenantLabels-modal">
        <v-card-title class="d-flex justify-space-between mb-2">
          <span v-if="this.hasCategory">{{$t('routes.labels.editCategory')}}</span>
          <span v-else>{{$t('routes.labels.createCategory')}}</span>

          <v-btn icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pt-4 scrollable" data-cy="dialog------">
          <div>
            <v-label>
              {{ $t('routes.labels.categoryName') }}:
            </v-label>
            <v-text-field
                v-model="categoryName"
                v-bind:label="$t('routes.labels.categoryName')"
                v-bind:rules="categoryNameRules"
                required block
                variant="solo-filled" flat
                data-cy="dialog-tenantLabels-categoryName">
            </v-text-field>

            <v-label>
              {{ $t('routes.labels.parentCategory') }}:
            </v-label>
            <v-select
                :items="[{ id: 'root', name: '- - -' }, ...flatCategories]"
                v-model="selectedCategory"
                item-title="name"
                item-value="id"
                variant="solo-filled" flat
                :label="$t('routes.labels.parentCategory')"
            >
              <option value="">---</option>
            </v-select>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            type="submit"
            class="bg-primary"
            elevation=0
            data-cy="dialog-tenantLabels-submitButton"
          >
            {{ hasCategory ? $t('common.update') : $t('common.create') }}
          </v-btn>

          <v-btn variant="text" @click="dialog = false" data-cy="dialog-tenantLabels-closeButton">
            {{$t('common.cancel')}}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useLabelStore } from '@/stores/LabelStore.js'
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  emits: ["categoryUpdated"],
  mixins: [DialogActionKeys],
  name: "CreateUpdateCategoryDialog",

  props: {
    category: {
      type: Object,
      required: true,
    },
    activeCategoryId: {
      type: String,
      default: '',
    },
  },

  data() {
    const namePattern = /^[\p{L}\p{N}\s-]{2,}$/u;

    return {
      dialog: false,

      errorMessage: "",
      errorTranslatable: null,

      categoryName: "",
      categoryNameRules: [
        v => !!v || this.$t('routes.labels.categoryNameRequired'),
        v => namePattern.test(v) || this.$t('routes.labels.invalidCategoryName'),
      ],
      selectedCategory: null,
    };
  },

  mounted: function() {
    this.resetForm();
  },

  watch: {
    dialog() {
      this.resetForm();
    },
    category: function() {
      this.resetForm();
    }
  },

  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.errorMessage = '';

        this.categoryName = '';
        this.selectedCategory = '';

        if (this.category && this.category.id) {
          this.categoryName = this.category?.name || '';

          if (this.category.parentid && this.category.parentid !== '') {
            this.selectedCategory = this.category.parentid
          }
        } else if (this.activeCategoryId && this.activeCategoryId !== '') {
          this.selectedCategory = this.activeCategoryId;
        }
      });

    },
    async updateCategory() {
      this.errorMessage = "";
      this.errorTranslatable = null;

      const validForm = await this.$refs.form.validate(); // validate is async in Vuetify 3
      if (!validForm.valid) { // Check the 'valid' property from the validation result
        return;
      }

      let payload = {
        name: this.categoryName,
        accessPointId: this.accessPointId,
        parentId: this.selectedCategory || null,
      };

      const labelStore = useLabelStore();
      let ACTION = labelStore.createCategory;

      if (this.category.id) {
        payload.categoryId = this.category.id;
        ACTION = labelStore.updateCategory;
      }

      try {
        await ACTION(payload);

        this.$emit('categoryUpdated');
        this.dialog = false;
      } catch (error) {
        const apiError = error?.response?.data?.error?.message;

        if (apiError) {
          this.$showMessage(apiError, { type: 'error' });
        } else {
          console.error("An unexpected error occurred:", error);
          // this.$showMessage('An unexpected error occurred', { type: 'error' }); // Add a generic message
        }
      }
    },
  },

  computed: {
    accessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    categories: function() {
      const labelStore = useLabelStore();
      return labelStore.getCategories;
    },
    flatCategories() {
      const flattenCategories = (categories, level = 0, skipCategoryId = null) => {
        return categories.reduce((acc, category) => {
          // Skip the current category if its ID matches the skipCategoryId
          if (category.id === skipCategoryId) {
            return acc;
          }

          let name = category.name;

          if (level > 0) {
            name = '└' + '─'.repeat(level * 2) + ' ' + category.name;
          }

          acc.push({
            ...category,
            name,
            level,
          });

          if (category.children) {
            acc = acc.concat(flattenCategories(category.children, level + 1, skipCategoryId));
          }

          return acc;
        }, []);
      };

      // Specify the category ID to skip
      const skipCategoryId = this.category.id || null;

      return flattenCategories(this.categories, 0, skipCategoryId);
    },
    hasCategory() {
      return !!(this.category && this.category.id);
    },
  }
};
</script>

<style>
</style>
